import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import BrandLogo from '@/components/BrandLogo';
import { Eye, EyeOff, Package, TrendingUp, Truck } from 'lucide-react';

function formatRegisterError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'object' && d?.msg ? d.msg : String(d))).join(' ') || 'Could not create account';
  }
  return 'Could not create account';
}

export default function Register() {
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const user = await register({
        email: email.trim(),
        password,
        name: name.trim(),
        company_name: companyName.trim() || undefined,
        phone: phone.trim() || undefined,
        country: country.trim() || undefined,
      });
      if (!user?.role) {
        setError('Account created but profile could not be loaded. Please sign in.');
        return;
      }
      /* PublicRoute redirects after register when user.role is set. */
    } catch (err) {
      setError(formatRegisterError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full h-11 px-3.5 rounded-lg border border-slate-300 text-sm bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-colors';
  const labelClass = 'block text-xs font-semibold text-slate-700 uppercase tracking-widest mb-1.5';

  return (
    <div className="min-h-screen flex bg-white">
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 max-w-lg w-full py-10">
        <div className="mb-8">
          <div className="mb-8">
            <BrandLogo variant="full" className="max-w-[min(100%,320px)]" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mb-2">Create account</h1>
          <p className="text-slate-500 text-sm font-body">Register as a customer to access the B2B portal</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <label className={labelClass} htmlFor="reg-name">Full name</label>
            <input
              id="reg-name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              data-testid="register-name-input"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-email">Email address</label>
            <input
              id="reg-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="register-email-input"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-company">Company (optional)</label>
            <input
              id="reg-company"
              type="text"
              autoComplete="organization"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Your company Ltd."
              data-testid="register-company-input"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="reg-phone">Phone (optional)</label>
              <input
                id="reg-phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 …"
                data-testid="register-phone-input"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="reg-country">Country (optional)</label>
              <input
                id="reg-country"
                type="text"
                autoComplete="country-name"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="United States"
                data-testid="register-country-input"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-password">Password</label>
            <div className="relative">
              <input
                id="reg-password"
                type={showPass ? 'text' : 'password'}
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                data-testid="register-password-input"
                className={`${inputClass} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="register-toggle-password"
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-confirm">Confirm password</label>
            <input
              id="reg-confirm"
              type={showPass ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat password"
              data-testid="register-confirm-input"
              className={inputClass}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="register-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-button"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating account…
              </>
            ) : (
              'Create account'
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-sky-600 hover:text-sky-700" data-testid="register-login-link">
            Sign in
          </Link>
        </p>
      </div>

      <div className="hidden lg:flex flex-1 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1765046255517-412341954c4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <BrandLogo variant="full" display="watermark" />
        <div className="relative z-10 max-w-md text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4 tracking-tight">
            Your Unified B2B<br />Command Center
          </h2>
          <p className="text-slate-400 text-sm font-body leading-relaxed mb-8">
            Manage all your trade operations, commodity bids, and logistics from one powerful platform.
          </p>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
              <div className="w-8 h-8 bg-sky-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-4 h-4 text-sky-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-semibold">DeMaxtore</p>
                <p className="text-slate-400 text-xs">Global B2B Supply & Export</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
              <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-orange-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-semibold">CommodityBid</p>
                <p className="text-slate-400 text-xs">Live Commodity Auctions</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Truck className="w-4 h-4 text-green-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-semibold">FreightIQ</p>
                <p className="text-slate-400 text-xs">Freight & Logistics Management</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
